import { Contact } from './contact';
import { Payment } from './payment';
import { BuildingCharacteristics } from './property-room-details';
import type { PEBRating, ComplianceStatus } from './property';
import type { UtilitySetup } from './utility-setup';

// Re-export BuildingCharacteristics for convenience
export { BuildingCharacteristics };

// Import the PropertyAddress interface directly
export interface PropertyAddress {
  line1: string;
  line2?: string;
  city: string;
  region?: string;
  zip: string;
  country: string;
}

/**
 * Enum for property group types
 */
export enum PropertyGroupType {
    Building = "building",
    Portfolio = "portfolio",
    Investment = "investment",
    Project = "project",
    Other = "other"
}

/**
 * Main PropertyGroup class representing a collection of properties or other groups
 */
export class PropertyGroup {
    // Mandatory fields
    groupUID!: string;
    ownerUID!: string;
    name!: string;
    type!: PropertyGroupType;
    
    // Optional fields
    description: string | null = null;
    parentGroupUID: string | null = null;  // Reference to a parent group if applicable
    
    // Address information (primarily for buildings)
    address?: PropertyAddress;
    
    // References to member properties and groups
    members: GroupMember[] = [];
    
    // Aggregated metrics (some may be calculated on demand rather than stored)
    metrics: GroupMetrics = {
        totalValue: null,
        totalEquity: null,
        totalDebt: null,
        occupancyRate: null,
        netMonthlyIncome: null,
        propertyCount: null,
        groupCount: null
    };
    
    // Group-specific fields
    financials: GroupFinancials = {
        loans: [],
        expenses: [],
        payments: []
    };
    
    // Contacts specifically related to this group
    contacts: GroupContacts = {
        manager: null,
        others: []
    };
    
    // Building-level characteristics (for building type groups)
    // These are inherited by all units within the building
    buildingCharacteristics?: BuildingCharacteristics | null = null;
    
    // Compteurs et contrats : commun / privatif par poste (#680).
    // Absent sur les immeubles existants — lire via `normalizeUtilities`.
    utilities?: UtilitySetup[] | null;

    // Building certificates (PEB, electrical, gas, etc.)
    certificates?: BuildingCertificates = {
        energyPerformance: [],
        electricalInspections: [],
        gasInspections: [],
        elevatorInspections: [],
        fireSafetyInspections: [],
        otherCertificates: []
    };
    
    // Building settings and information
    settings?: BuildingSettings = {
        syndic: null,
        propertyRules: null,
        buildingInsurance: null,
        commonCharges: null,
        parkingInfo: null
    };
    
    // Metadata
    createdAt: Date = new Date();
    updatedAt: Date = new Date();
    deleted = false;
    deletedAt: Date | null = null;

    /**
     * Constructor for PropertyGroup
     */
    constructor(groupData: Partial<PropertyGroup> = {}) {
        Object.assign(this, groupData);
        
        // Handle Firestore Timestamp objects for dates
        if (this.createdAt && typeof this.createdAt === 'object' && 'toDate' in this.createdAt && 
            typeof this.createdAt.toDate === 'function') {
            this.createdAt = this.createdAt.toDate();
        }
        
        if (this.updatedAt && typeof this.updatedAt === 'object' && 'toDate' in this.updatedAt && 
            typeof this.updatedAt.toDate === 'function') {
            this.updatedAt = this.updatedAt.toDate();
        }
        
        if (this.deletedAt && typeof this.deletedAt === 'object' && 'toDate' in this.deletedAt && 
            typeof this.deletedAt.toDate === 'function') {
            this.deletedAt = this.deletedAt.toDate();
        }
    }
    
    /**
     * Calculate aggregated metrics for the group
     * This method would be implemented later to calculate actual metrics
     * based on member properties and/or groups
     */
    calculateMetrics(): void {
        // Placeholder for future implementation
        // Would calculate metrics based on members
    }
}

/**
 * Interface representing a member of a property group
 * This can be either a property or another group
 */
export interface GroupMember {
    id: string;
    type: 'property' | 'group';
    addedAt: Date;
}

/**
 * Interface for group metrics
 */
export interface GroupMetrics {
    totalValue: number | null;
    totalEquity: number | null;
    totalDebt: number | null;
    occupancyRate: number | null;
    netMonthlyIncome: number | null;
    propertyCount: number | null;
    groupCount: number | null;
}

/**
 * Interface for group financial information
 */
export interface GroupFinancials {
    loans: string[];  // Array of loan UIDs that are associated with the group as a whole
    expenses: string[];  // Array of expense UIDs
    payments: Payment[];  // Payments directly associated with the group
}

/**
 * Interface for group contacts
 */
export interface GroupContacts {
    manager: Contact | null;
    others: Contact[];
}

/**
 * Certificate/Inspection record
 */
export interface CertificateRecord {
    id: string;
    type: string; // e.g., 'PEB A+', 'Electrical Compliance'
    issueDate: Date;
    expiryDate?: Date;
    certificateNumber?: string;
    issuedBy?: string;
    documentUrl?: string;
    documentUID?: string;
    notes?: string;
    status: 'valid' | 'expiring_soon' | 'expired' | 'pending';

    // Optional structured fields, populated when the shared certificate dialog
    // (the same one used for units) captures PEB / technical-inspection details
    // for a building's common-area certificate. Purely additive: legacy records
    // simply omit them. The flat fields above stay the source of truth for the
    // list/summary; these enrich the detail view and round-trip the rich dialog.
    rating?: PEBRating;                    // PEB energy rating (A++…G)
    primaryEnergyConsumption?: number;     // kWh/m²/year (PEB)
    co2Emissions?: number;                 // kg CO₂/m²/year (PEB)
    complianceStatus?: ComplianceStatus;   // technical inspection outcome
    inspection?: {
        inspectorName?: string;
        inspectorLicense?: string;
        inspectionDate?: Date;
    };
}

/**
 * Building Certificates
 */
export interface BuildingCertificates {
    energyPerformance: CertificateRecord[];
    electricalInspections: CertificateRecord[];
    gasInspections: CertificateRecord[];
    elevatorInspections: CertificateRecord[];
    fireSafetyInspections: CertificateRecord[];
    otherCertificates: CertificateRecord[];
}

/**
 * A single syndic contact person (used by the extended multi-contact dialog).
 */
export interface SyndicContactPerson {
    name: string;
    role?: string;
    phone?: string;
    email?: string;
}

/**
 * Syndic / Building Management Information — CANONICAL type.
 *
 * Unifies the former building-level `SyndicInfo` and the property-level
 * `PropertySyndicInfo` (now a re-exported alias in property.ts). Superset of both:
 * all fields optional, with the legacy single-contact fields, the extended general
 * company contact, the multi-contact array, and the building-level `monthlyFee`.
 * Used on `PropertyGroup.settings.syndic` AND `Property.characteristics.syndicInfo`.
 */
export interface SyndicInfo {
    companyName?: string;
    contactPerson?: string;   // Legacy single-contact name (kept for backward compat)
    phone?: string;           // Legacy single-contact phone
    email?: string;           // Legacy single-contact email
    address?: string;
    contractStartDate?: Date;
    contractEndDate?: Date;
    monthlyFee?: number;      // Building-level recurring fee
    notes?: string;

    // Extended fields
    generalPhone?: string;
    generalEmail?: string;
    contactPersons?: SyndicContactPerson[];
}

/**
 * Common Charges Information
 */
export interface CommonChargesInfo {
    monthlyAmount?: number;
    frequency: 'monthly' | 'quarterly' | 'yearly';
    includes: string[]; // List of what's included (e.g., ['water', 'heating', 'elevator maintenance'])
    notes?: string;
}

/**
 * Building Insurance Information
 */
export interface BuildingInsuranceInfo {
    provider: string;
    policyNumber?: string;
    coverage?: number;
    annualPremium?: number;
    startDate?: Date;
    renewalDate?: Date;
    contactEmail?: string;
    contactPhone?: string;
    notes?: string;
}

/**
 * Parking Information
 */
export interface ParkingInfo {
    totalSpaces?: number;
    reservedSpaces?: number;
    visitorSpaces?: number;
    motorcycleSpaces?: number;
    evChargingStations?: number;
    parkingType: 'indoor' | 'outdoor' | 'mixed';
    notes?: string;
}

/**
 * Building Settings
 */
export interface BuildingSettings {
    syndic: SyndicInfo | null;
    propertyRules: string | null; // Can be markdown text or URL to document
    buildingInsurance: BuildingInsuranceInfo | null;
    commonCharges: CommonChargesInfo | null;
    parkingInfo: ParkingInfo | null;
} 